import { t } from 'i18next';
import React, {
  useEffect, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Loader } from '~/components';
import { generateNameFormatted } from '~/helpers/titleHelper';
import { AnalysisLayout } from '~/pages/Analysis/AnalysisLayout';
import { apiCallDownload } from '~/providers';
import { toast } from 'react-toastify';
import { getUserProfile } from 'helpers/userProfile';
import {
  BottomLayer,
  ButtonConfort,
  ButtonExport,
  ButtonVerDetalhes,
  Container,
  ContainerLoader,
  FilterWarningBanner,
  Toolbar,
  ToolbarRight,
} from './style';

const THERMAL_COMFORT_BI_URL = 'https://app.powerbi.com/view?r=eyJrIjoiYjEwZmFiNmItYzdmNi00ZGI5LWEyZWQtODYwNzI4NmQxNGUyIiwidCI6IjNlNmM3M2UzLTkxYzctNGQ2Yy1iZjc1LWNjZDUwOThmM2JhMyJ9';

// Tipos de card que NÃO devem ter botão Ver Detalhes
const CARDS_WITHOUT_DETAILS = ['offline', 'bignumber'];

export const ThermalComfortDashboard: React.FC = () => {
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [detailsUrl, setDetailsUrl] = useState<string | null>(null);
  const [showFilterWarning, setShowFilterWarning] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [profile] = useState(getUserProfile);
  const history = useHistory();

  useEffect(() => {
    if (!profile.permissions.isAdminSistema) {
      history.replace('/analise/unidades');
    }
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'verDetalhes'
        && !CARDS_WITHOUT_DETAILS.includes(event.data?.cardType)
        && event.data?.url
      ) {
        setDetailsUrl(event.data.url);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsReady(true), 5000);
  };

  const handleIframeError = () => {
    setHasError(true);
    setIsReady(false);
  };

  useEffect(() => () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowFilterWarning(false), 10000);
    return () => clearTimeout(timer);
  }, []);

  async function handleExport() {
    try {
      setIsExporting(true);
      toast.info(t('donwloadIniado'));
      const clientIds = profile.permissions.PER_CLIENT.map((x) => x.clientId);
      const exportResponse = await apiCallDownload('/thermal-comfort/export', { CLIENT_IDS: clientIds });
      const link = document.getElementById('thermalComfortDownloadLink') as HTMLAnchorElement;
      if (link.href !== '#') window.URL.revokeObjectURL(link.href);
      link.href = window.URL.createObjectURL(exportResponse.data);
      link.download = exportResponse.headers.filename || `${t('confortoTermico')}.xlsx`;
      link.click();
      toast.success(t('sucessoExportacao'));
    } catch {
      toast.error(t('naoFoiPossivelExportar'));
    } finally {
      setIsExporting(false);
    }
  }

  function handleVerDetalhes() {
    if (detailsUrl) {
      history.push(detailsUrl);
    }
  }

  if (!profile.permissions.isAdminSistema) return null;

  return (
    <>
      <Helmet>
        <title>{generateNameFormatted('Celsius 360', t('confortoTermico'))}</title>
      </Helmet>
      <AnalysisLayout />
      <a href="#" style={{ display: 'none' }} id="thermalComfortDownloadLink" />
      <Container>
        {showFilterWarning && (
          <FilterWarningBanner>
            <InfoCircledIcon width={16} height={16} />
            {t('avisoFiltrosCelsiusNaoConectadosBi')}
            <ButtonConfort
              type="button"
              onClick={() => setShowFilterWarning(false)}
              aria-label="Fechar aviso"
            >
              ×
            </ButtonConfort>
          </FilterWarningBanner>
        )}
        <Toolbar>
          <div />
          <ToolbarRight>
            {detailsUrl && (
              <ButtonVerDetalhes onClick={handleVerDetalhes}>
                {t('verDetalhes')}
              </ButtonVerDetalhes>
            )}
            <ButtonExport onClick={handleExport} disabled={isExporting}>
              {t('exportar')}
            </ButtonExport>
          </ToolbarRight>
        </Toolbar>
        <iframe
          key={THERMAL_COMFORT_BI_URL}
          ref={iframeRef}
          title={t('confortoTermico')}
          src={THERMAL_COMFORT_BI_URL}
          width="100%"
          height="100%"
          style={{
            border: 'none',
            transform: 'scale(1.01)',
            transformOrigin: 'top center',
            display: hasError || !isReady ? 'none' : 'block',
          }}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          onContextMenu={(e) => e.preventDefault()}
        />
        {isReady && <BottomLayer />}
        {!isReady && !hasError && (
          <ContainerLoader>
            <Loader />
            <p>{t('carregandoDados')}</p>
          </ContainerLoader>
        )}
        {hasError && (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <p>{t('erroRelatorio')}</p>
          </div>
        )}
      </Container>
    </>
  );
};
